import { useMemo } from 'react'
import { useStepResultsState } from './useStepResultsState'
import { createDeleteDayModal } from '../components/DeleteDayModal'
import { createDeleteLocationModal } from '../components/DeleteLocationModal'
import {
  convertTimeToSimpleFormat,
  handleLocationChange,
  handleMissionBaChange,
  handleMissionTimeChange,
  handleApplyGlobalBaPerMission,
  handleDeleteDay,
  handleDeleteLocation,
  handleCancel,
  handleSave,
} from '../utils/'

export const useStepResultsLogic = (
  data,
  runSnackBar,
  city,
  setData,
  isEdit,
  setIsEdit,
  setModalProps,
  campaignId,
) => {
  const state = useStepResultsState(data, isEdit)

  // Create event handlers
  const handlers = {
    handleLocationChange: handleLocationChange(
      state.setCurrentData,
      state.savedData,
    ),

    handleMissionBaChange: handleMissionBaChange(state.setCurrentData),

    handleMissionTimeChange: handleMissionTimeChange(
      state.setCurrentData,
      convertTimeToSimpleFormat,
      city,
    ),

    handleApplyGlobalBaPerMission: handleApplyGlobalBaPerMission(
      state.setCurrentData,
      state.baPerMission,
      runSnackBar,
    ),

    handleDeleteDay: handleDeleteDay(
      state.setCurrentData,
      state.onCloseModal,
      runSnackBar,
    ),

    handleDeleteLocation: handleDeleteLocation(
      state.setCurrentData,
      state.onCloseModal,
      runSnackBar,
    ),

    handleCancel: handleCancel(
      state.savedData,
      state.originalBaPerMission,
      state.originalPayAdjustment,
      state.setCurrentData,
      state.handleBaPerMissionChange,
      state.handlePayAdjustmentChange,
      setIsEdit,
      setData,
    ),

    handleSave: handleSave(
      state.currentData,
      state.baPerMission,
      state.payAdjustment,
      state.setSavedData,
      state.setOriginalBaPerMission,
      state.setOriginalPayAdjustment,
      setIsEdit,
      runSnackBar,
      setData,
      city,
      setModalProps,
      state.setCurrentData,
      campaignId,
    ),

    onClickDeleteDay: day => {
      state.setModalProps(
        createDeleteDayModal(day, state.onCloseModal, handlers.handleDeleteDay),
      )
    },

    onClickDeleteLocation: (location, dayId) => {
      state.setModalProps(
        createDeleteLocationModal(
          location,
          dayId,
          state.onCloseModal,
          handlers.handleDeleteLocation,
        ),
      )
    },
  }

  // Rating and feedback handlers (local updates in state)
  const handleMissionRatingChange = (dayId, missionIndex, nextRating) => {
    state.setCurrentData(prev => {
      const updated = prev.map(day => {
        if (day.id !== dayId) return day
        const missions = day.missions.map((m, idx) => {
          if (idx !== missionIndex) return m
          const toggled =
            m.rating && m.rating === nextRating ? null : nextRating
          return {
            ...m,
            rating: toggled,
            // if rating switches away from down, clear feedback
            feedback: toggled === 'down' ? m.feedback || '' : '',
          }
        })
        return { ...day, missions }
      })
      // propagate to parent so Approve validation sees ratings immediately
      setData(updated)
      return updated
    })
  }

  const handleMissionFeedbackChange = (dayId, missionIndex, text) => {
    state.setCurrentData(prev => {
      const updated = prev.map(day => {
        if (day.id !== dayId) return day
        const missions = day.missions.map((m, idx) => {
          if (idx !== missionIndex) return m
          return {
            ...m,
            feedback: text.slice(0, 140),
          }
        })
        return { ...day, missions }
      })
      // propagate to parent so Approve validation reflects feedback too
      setData(updated)
      return updated
    })
  }

  const ratingStats = useMemo(() => {
    const data = state.currentData || []
    let totalMissionsCount = 0
    let ratedCount = 0
    data.forEach(day => {
      const missions = Array.isArray(day.missions) ? day.missions : []
      totalMissionsCount += missions.length
      missions.forEach(m => {
        if (m.rating === 'up' || (m.rating === 'down' && m.feedback)) {
          ratedCount += 1
        }
      })
    })
    const requiredRatings = Math.max(1, Math.ceil(totalMissionsCount * 0.25))
    return {
      totalMissionsCount,
      ratedCount,
      requiredRatings,
      isRatingThresholdMet: ratedCount >= requiredRatings,
    }
  }, [state.currentData])

  return {
    ...state,
    ...handlers,
    handleMissionRatingChange,
    handleMissionFeedbackChange,
    ...ratingStats,
  }
}
